import { Button } from "./button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faLockOpen, faUserSecret } from "@fortawesome/free-solid-svg-icons";
import React, { useCallback, useEffect, useRef, useState } from "react";

export const UsersTableRow: React.FC<{
  key: string;
  index: number;
  user: {
    roles?: string[];
    password?: string;
    section?: string;
    accounts?: {[key: string]: any};
    account?: string;
    name?: string;
    address?: string;
    phones?: {[key: string]: any};
  };
  admin: {
    roles?: string[];
    password?: string;
    section?: string;
    accounts?: {[key: string]: any};
    account?: string;
    name?: string;
    address?: string;
    phones?: {[key: string]: any};
  };
  editUser: (account?: string) => () => void;
  resetPasswordUser: (account?: string) => () => void;
  impersonateUser: (account?: string) => () => void;
}> = React.memo(({ key, index, user, admin, editUser, resetPasswordUser, impersonateUser }) => {
  const [isVisible, setIsVisible] = useState(false);
  const rowRef = useRef<HTMLTableRowElement>(null);

  // Memoize the IntersectionObserver callback
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.target === rowRef.current) {
          setIsVisible(entry.isIntersecting);
        }
      });
    },
    []
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      root: null, // Observe within the viewport
      threshold: 0.1, // 10% of the row must be visible
    });

    const rowElement = rowRef.current;
    if (rowElement) {
      observer.observe(rowElement);
    }

    return () => {
      if (rowElement) {
        observer.unobserve(rowElement);
      }
    };
  }, [handleIntersection]);

  if (!isVisible) {
    return <tr ref={rowRef}>
      <td colSpan={6} className={"text-center bg-gray-50"}>&nbsp;</td>
    </tr>; // Optionally, return a placeholder while the row is not visible
  }

  let phoneCount = 0;
  const isAdmin = user?.roles?.[0] === "ADMIN";
  const isPasswordEmpty = user?.password === "N";

  const confirmReset = (selectedUser: any) => () => {
    const isToReset = window.confirm("Ви впевнені, що хочете скинути пароль?");
    if (isToReset) {
      resetPasswordUser(selectedUser?.account)();
    }
  };

  return (
    <tr
      ref={rowRef}
      key={key}
      className={`${isAdmin ? 'text-gray-900 bg-white' : 'text-gray-900 bg-white'}
                  ${isPasswordEmpty ? 'bg-green-100' : 'bg-white'} hover:bg-gray-50`}
    >
      <td className="w-[40px] text-center px-2 border border-gray-200 break-words">
        {isAdmin ? <span className="text-red-500">{index + 1}</span> : (index + 1)}
      </td>
      <td className="text-left px-2 border border-gray-200 break-words">
        {user.section}
      </td>
      <td className="text-left px-2 border border-gray-200 break-words">
        {user?.accounts?.["raw"] || user?.account || ""}
        <br/>
        {isAdmin && <span className="text-red-500">Адміністратор</span>}
      </td>
      <td className="text-left px-2 border border-gray-200 break-words">
        {user.name}
        <br/>
        {user?.address || ""}
        <br/>
        {isAdmin && <span className="text-red-500">Адміністратор</span>}
      </td>
      <td className="text-left px-2 border border-gray-200 break-words">
        {isPasswordEmpty ? <><span className="text-red-500">Пароль не встановлено</span><br/></> : <></>}
        {Object.entries(user?.phones || {}).map(([key, phone]: [string, any], index) => {
          if (key !== "raw" && phone !== "") {
            phoneCount++;
            return (<>{phone}<br/></>);
          }
          return;
        })}
        {phoneCount === 0 ? <><span className="text-red-700">Телефона не маэ</span><br/></> : <></>}
      </td>
      <td className="text-left px-2 border border-gray-200 break-words">
        {admin?.roles?.includes("ADMIN") && (
          <>
            <Button className="ml-1 px-2" onClick={confirmReset(user)}>
              <FontAwesomeIcon icon={faLockOpen}/>
            </Button>
            <Button className="ml-1 px-2" onClick={editUser(user?.account)}>
              <FontAwesomeIcon icon={faEdit}/>
            </Button>
          </>
        )}
        <Button className="ml-1 px-2" onClick={impersonateUser(user?.account)}>
          <FontAwesomeIcon icon={faUserSecret}/>
        </Button>
      </td>
    </tr>
  );
});
